import { useCallback } from 'react';
import { create } from 'zustand';
import { getTriggerSubTypes } from 'services/asset';
import { unionBy } from 'lodash';

// ======================
// Types
// ======================

export interface TriggerSubTypeOption {
  label: string;
  value: string; // trigger sub type id, stringified — matches the id-string
  // convention used by other id-backed form fields (e.g. `manufacture`, `type`)
  // Read-only Unit display for Runtime maintenance configs — Runtime doesn't
  // pick a unit from /maintenance-units, it shows this instead.
  defaultUnit?: string | null;
}

interface RawTriggerSubType {
  id: number;
  name: string;
  defaultUnit?: string | null;
}

// A maintenance schedule's `triggerSubTypeId` is submitted as just the sub type
// id (a plain number, also what GET responses return for this same field —
// GET separately includes a populated `triggerSubType: { id, name, defaultUnit }`
// relation for display purposes only). Normalize to the id-as-string used
// throughout form state, tolerating an object shape too in case one is ever
// passed in directly (e.g. from the `triggerSubType` relation).
export const normalizeTriggerSubTypeId = (
  raw: { id: number } | number | string | null | undefined
): string | null => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return String(raw.id);
  return String(raw);
};

interface TriggerSubTypeStore {
  triggerSubTypes: TriggerSubTypeOption[];
  setTriggerSubTypes: (types: TriggerSubTypeOption[]) => void;
}

// ======================
// Store
// ======================

export const useTriggerSubTypeStore = create<TriggerSubTypeStore>((set) => ({
  triggerSubTypes: [],
  setTriggerSubTypes: (triggerSubTypes) => set({ triggerSubTypes })
}));

// ======================
// Helpers
// ======================

// mirrors mapTriggerTypeResponse in triggerType.tsx
// API returns { id, name, defaultUnit } (e.g. Vibration, Temperature, Voltage)
// label = name, value = id (stringified) — saved schedule submits maintenance_sub_type as just the id
const mapTriggerSubTypeResponse = (raw: RawTriggerSubType[]): TriggerSubTypeOption[] => {
  return raw.map((item) => ({
    label: item.name,
    value: String(item.id),
    defaultUnit: item.defaultUnit ?? null
  }));
};

// ======================
// Hooks
// ======================

/**
 * Hook to fetch trigger sub types — mirrors useFetchTriggerTypes
 */
export const useFetchTriggerSubTypes = () => {
  const { setTriggerSubTypes } = useTriggerSubTypeStore();

  const init = useCallback(async () => {
    try {
      const data = await getTriggerSubTypes();
      setTriggerSubTypes(mapTriggerSubTypeResponse(data.triggerSubTypes));
    } catch (error) {
      console.error('Failed to fetch trigger sub types:', error);
    }
  }, [setTriggerSubTypes]);

  return init;
};

/**
 * Hook to force-insert a trigger sub type into the store during edit
 * mirrors useInsertOptionTriggerType
 */
export const useInsertOptionTriggerSubType = () => {
  const insert = useCallback((option: TriggerSubTypeOption) => {
    const { triggerSubTypes, setTriggerSubTypes } = useTriggerSubTypeStore.getState();
    const updated = unionBy([option], triggerSubTypes, 'value');
    setTriggerSubTypes(updated);
  }, []);

  return insert;
};
